/* Small, dependency-free carousel shared by the homepage hero and reviews. */
(() => {
  if (customElements.get('pp-carousel')) return;
  customElements.define('pp-carousel', class extends HTMLElement {
    connectedCallback() { queueMicrotask(() => this.setup()); }
    setup() {
      if (!this.isConnected || this.abort) return;
      this.track = this.querySelector('[data-pp-track]');
      this.slides = [...this.querySelectorAll('[data-pp-slide]')];
      if (!this.track || !this.slides.length) return;
      this.abort = new AbortController();
      const on = (node, type, fn, options = {}) => node?.addEventListener(type, fn, {...options, signal:this.abort.signal});
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.stopped = this.dataset.autoplay !== 'true' || this.motion.matches;
      this.hovered = false; this.inView = false; this.index = 0;
      this.delay = Math.max(5000, Math.min(20000, Number(this.dataset.interval) || 7000));
      this.style.setProperty('--pp-slide-duration', `${this.delay}ms`);
      this.setAttribute('role', 'region'); this.setAttribute('aria-roledescription', 'karrusel');
      const pagination = this.querySelector('[data-pp-pagination]');
      this.dots = this.slides.map((slide, i) => {
        slide.setAttribute('role','group');slide.setAttribute('aria-roledescription','slide');
        slide.setAttribute('aria-label',`${i+1} af ${this.slides.length}: ${slide.dataset.label || ''}`);
        const button = document.createElement('button');
        button.type = 'button';button.className = 'pp-slide-link';
        button.textContent = slide.dataset.label || String(i+1);
        button.setAttribute('aria-label',`Vis ${i+1}: ${slide.dataset.label || 'slide'}`);
        button.setAttribute('aria-controls',slide.id);
        on(button,'click',()=>this.select(i,true));return button;
      });
      pagination?.replaceChildren(...this.dots);
      this.querySelector('[data-pp-controls]').hidden = this.slides.length < 2;
      this.dataset.ready = 'true';
      on(this.querySelector('[data-pp-prev]'),'click',()=>this.select(this.index-1,true));
      on(this.querySelector('[data-pp-next]'),'click',()=>this.select(this.index+1,true));
      on(this.querySelector('[data-pp-play]'),'click',()=>{this.stopped=!this.stopped;this.schedule();});
      on(this,'mouseenter',()=>{this.hovered=true;this.schedule();});
      on(this,'mouseleave',()=>{this.hovered=false;this.schedule();});
      on(this,'focusin',event=>{
        // Keyboard interaction pauses rotation until the visitor explicitly starts it.
        if (!event.target.closest('[data-pp-play]')) {this.stopped=true;this.schedule();}
      });
      on(this,'keydown',event=>{
        if (event.target.closest('input,textarea,select,[contenteditable="true"]')) return;
        if (event.key==='ArrowRight' || event.key==='ArrowLeft') {
          event.preventDefault();const moveFocus = this.track.contains(document.activeElement);
          this.select(this.index+(event.key==='ArrowRight'?1:-1),true);
          if(moveFocus)this.dots[this.index].focus();
        }
      });
      on(this.track,'pointerdown',event=>{
        if(event.pointerType==='mouse' || event.isPrimary===false)return;
        this.touch={x:event.clientX,y:event.clientY};this.dragged=false;
      });
      on(this.track,'pointerup',event=>{
        if(!this.touch)return;
        const x=event.clientX-this.touch.x,y=event.clientY-this.touch.y;this.touch=null;
        if(Math.abs(x)>45 && Math.abs(x)>Math.abs(y)*1.4){
          this.dragged=true;this.select(this.index+(x<0?1:-1),true);
          clearTimeout(this.dragTimer);this.dragTimer=setTimeout(()=>{this.dragged=false},350);
        }
      });
      on(this.track,'pointercancel',()=>{this.touch=null;});
      on(this.track,'click',event=>{if(this.dragged){event.preventDefault();event.stopPropagation();this.dragged=false;}},{capture:true});
      on(document,'visibilitychange',()=>this.schedule());
      on(this.motion,'change',()=>{if(this.motion.matches)this.stopped=true;this.schedule();});
      on(this,'shopify:block:select',event=>{
        const i=this.slides.findIndex(s=>s===event.target || s.contains(event.target));
        if(i>=0)this.select(i,true);
      });
      this.observer = new IntersectionObserver(entries=>{
        this.inView=entries[0].isIntersecting;this.schedule();
      },{threshold:0.15});
      this.observer.observe(this);
      this.select(0);
    }
    select(index,manual=false) {
      if(!this.abort)return;
      if(manual)this.stopped=true;
      this.index=(index+this.slides.length)%this.slides.length;
      this.slides.forEach((slide,i)=>{
        const active=i===this.index;
        slide.dataset.active=String(active);slide.inert=!active;
        slide.setAttribute('aria-hidden',String(!active));
        this.dots[i].setAttribute('aria-current',String(active));
      });
      this.querySelector('[data-pp-count]').textContent=`${String(this.index+1).padStart(2,'0')} / ${String(this.slides.length).padStart(2,'0')}`;
      if(manual)this.querySelector('[data-pp-status]').textContent=`${this.index+1} af ${this.slides.length}: ${this.slides[this.index].dataset.label || ''}`;
      this.schedule();
    }
    schedule() {
      clearTimeout(this.timer);this.dataset.running='false';
      if(!this.abort)return;
      const play=this.querySelector('[data-pp-play]');
      play.setAttribute('aria-label',this.stopped?'Start automatisk skift':'Sæt automatisk skift på pause');
      this.querySelector('[data-pp-play-icon]').textContent=this.stopped?'▷':'Ⅱ';
      if(this.stopped || this.hovered || !this.inView || document.hidden || this.slides.length<2)return;
      // Reset the thin progress line in the same frame as the actual timer.
      this.dots[this.index]?.getBoundingClientRect();this.dataset.running='true';
      this.timer=setTimeout(()=>this.select(this.index+1),this.delay);
    }
    disconnectedCallback() {
      clearTimeout(this.timer);clearTimeout(this.dragTimer);this.observer?.disconnect();this.abort?.abort();this.abort=null;
      delete this.dataset.ready;delete this.dataset.running;
      this.slides?.forEach(slide=>{slide.inert=false;slide.removeAttribute('aria-hidden');});
    }
  });
})();
